/**
 * Attractions Redux Slice
 * Manages attraction data and search results with normalized state and caching
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  searchAttractions,
  searchVenues,
  getAllAttractions,
  getAttractionById,
} from '../util/api/supabaseApi';

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

// Async thunks
export const fetchAllAttractions = createAsyncThunk(
  'attractions/fetchAll',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const cached = state.attractions?.lastFetched;

      // Return cached if fresh (within 10 minutes for all attractions)
      if (cached && Date.now() - cached < 10 * 60 * 1000) {
        const allAttractions = Object.values(state.attractions?.byId || {});
        if (allAttractions.length > 0) {
          return allAttractions;
        }
      }

      const attractions = await getAllAttractions();
      return attractions;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Enhanced search thunk with caching
export const searchAttractionsThunk = query => async (dispatch, getState, sdk) => {
  const normalizedQuery = query?.toLowerCase().trim();
  const state = getState();
  const cached = state.attractions?.searchCache?.[normalizedQuery];

  // Check cache first
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    // Return cached results
    const cachedAttractions = cached.ids.map(id => state.attractions?.byId?.[id]).filter(Boolean);

    if (cachedAttractions.length > 0) {
      dispatch({
        type: 'attractions/search/fulfilled',
        payload: cachedAttractions,
        meta: { cached: true },
      });
      return cachedAttractions;
    }
  }

  dispatch({ type: 'attractions/search/pending', meta: { query: normalizedQuery } });

  try {
    // Pass SDK to searchAttractions so it can count ShareTribe events
    // We execute both searches concurrently and catch errors so one failure doesn't block the other
    const [attractions, venues] = await Promise.all([
      searchAttractions(query, sdk).catch(e => {
        console.error('Attraction search error:', e);
        return [];
      }),
      searchVenues(query).catch(e => {
        console.error('Venue search error:', e);
        return [];
      }),
    ]);

    // Tag venues and format their category to be 'Venue' -> matching EventAutocomplete styling
    const formattedVenues = venues.map(v => ({
      ...v,
      isVenue: true,
      category: [v.city, v.state].filter(Boolean).join(', ') || 'Venue',
    }));

    const results = [...attractions, ...formattedVenues];

    dispatch({
      type: 'attractions/search/fulfilled',
      payload: results,
      meta: { query: normalizedQuery },
    });
    return results;
  } catch (error) {
    const errorMessage = error?.message || 'Failed to search attractions';
    dispatch({ type: 'attractions/search/rejected', payload: errorMessage });
    throw error;
  }
};

export const fetchAttractionById = createAsyncThunk(
  'attractions/fetchById',
  async (attractionId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const cached = state.attractions?.byId?.[attractionId];

      // Return cached if exists
      if (cached) {
        return cached;
      }

      const attraction = await getAttractionById(attractionId);
      return attraction;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const attractionsSlice = createSlice({
  name: 'attractions',
  initialState: {
    byId: {}, // Normalized: { [id]: attraction }
    searchCache: {}, // { [query]: { ids: [], timestamp: number } }
    selectedAttraction: null,
    loading: false,
    searchLoading: false,
    error: null,
    lastFetched: null,
    // Legacy support
    allAttractions: [],
    searchResults: [],
  },
  reducers: {
    clearSearchResults: state => {
      state.searchResults = [];
      state.searchCache = {};
    },
    clearSelectedAttraction: state => {
      state.selectedAttraction = null;
    },
    clearSearchCache: state => {
      state.searchCache = {};
    },
    // Clean expired cache entries
    cleanExpiredCache: state => {
      const now = Date.now();
      Object.keys(state.searchCache).forEach(query => {
        if (now - state.searchCache[query].timestamp > CACHE_TTL) {
          delete state.searchCache[query];
        }
      });
    },
  },
  extraReducers: builder => {
    builder
      // Fetch all attractions
      .addCase(fetchAllAttractions.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAttractions.fulfilled, (state, action) => {
        state.loading = false;
        const attractions = action.payload;

        // Normalize: store by ID
        attractions.forEach(attr => {
          state.byId[attr.id] = attr;
        });

        // Legacy support
        state.allAttractions = attractions;
        state.lastFetched = Date.now();
      })
      .addCase(fetchAllAttractions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch attraction by ID
      .addCase(fetchAttractionById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttractionById.fulfilled, (state, action) => {
        state.loading = false;
        const attraction = action.payload;

        // Normalize: store by ID
        state.byId[attraction.id] = attraction;
        state.selectedAttraction = attraction;
      })
      .addCase(fetchAttractionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search attractions - handle regular thunk actions (must be after all addCase calls)
      .addMatcher(
        action => action && action.type === 'attractions/search/pending',
        (state, action) => {
          // Only set loading if not using cache
          if (!action.meta?.cached) {
            state.searchLoading = true;
          }
          state.error = null;
        }
      )
      .addMatcher(
        action => action && action.type === 'attractions/search/fulfilled',
        (state, action) => {
          state.searchLoading = false;
          const attractions = action.payload;
          const query = action.meta?.query;

          // Normalize: store by ID
          attractions.forEach(attr => {
            state.byId[attr.id] = attr;
          });

          // Cache search results
          if (query) {
            state.searchCache[query] = {
              ids: attractions.map(a => a.id),
              timestamp: Date.now(),
              query,
            };
          }

          // Legacy support
          state.searchResults = attractions;
        }
      )
      .addMatcher(
        action => action && action.type === 'attractions/search/rejected',
        (state, action) => {
          state.searchLoading = false;
          state.error = action.payload;
        }
      );
  },
});

// Export actions
export const clearSearchResults = attractionsSlice.actions.clearSearchResults;
export const clearSelectedAttraction = attractionsSlice.actions.clearSelectedAttraction;
export const clearSearchCache = attractionsSlice.actions.clearSearchCache;
export const cleanExpiredCache = attractionsSlice.actions.cleanExpiredCache;

export default attractionsSlice.reducer;
